import React, { useEffect, useRef } from 'react';
import { Univer, UniverInstanceType, LocaleType } from '@univerjs/core';
import { createWorkbookWithData } from '../lib/univer-setup';
import { defaultTheme } from '@univerjs/design';
import { UniverDocsPlugin } from '@univerjs/docs';
import { UniverDocsUIPlugin } from '@univerjs/docs-ui';
import { UniverFormulaEnginePlugin } from '@univerjs/engine-formula';
import { UniverRenderEnginePlugin } from '@univerjs/engine-render';
import { UniverSheetsPlugin } from '@univerjs/sheets';
import { UniverSheetsFormulaPlugin } from '@univerjs/sheets-formula';
import { UniverSheetsUIPlugin } from '@univerjs/sheets-ui';
import { UniverUIPlugin } from '@univerjs/ui';

import '@univerjs/design/lib/index.css';
import '@univerjs/ui/lib/index.css';
import '@univerjs/sheets-ui/lib/index.css';

export const UniverSimpleTest: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const univerRef = useRef<Univer | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (univerRef.current) return;

    const initUniver = async () => {
      console.log('🚀 Initializing Univer Simple Test');

      // Create Univer instance
      const univer = new Univer({
        theme: defaultTheme,
        locale: LocaleType.EN_US,
        locales: {
          [LocaleType.EN_US]: {},
        },
      });

      univerRef.current = univer;

      // Register plugins
      console.log('🔌 Registering plugins...');
      univer.registerPlugin(UniverRenderEnginePlugin);
      univer.registerPlugin(UniverUIPlugin, {
        container: containerRef.current!,
      });
      univer.registerPlugin(UniverDocsPlugin);
      univer.registerPlugin(UniverDocsUIPlugin);
      univer.registerPlugin(UniverSheetsPlugin);
      univer.registerPlugin(UniverSheetsUIPlugin);
      univer.registerPlugin(UniverFormulaEnginePlugin);
      univer.registerPlugin(UniverSheetsFormulaPlugin);

      console.log('✅ Plugins registered');

      // Load 100-row dataset
      const realData = await fetch('/100-rows-data.json').then(r => r.json());
    console.log('📊 Loaded real data:', realData.rows.length, 'rows');
    
    // Convert to Univer format
    const cellData: Record<number, Record<number, any>> = {};
    realData.rows.forEach((row: any[], rowIndex: number) => {
      if (Array.isArray(row)) {
        row.forEach((cell: any, colIndex: number) => {
          if (cell && cell.v !== null && cell.v !== undefined) {
            if (!cellData[rowIndex]) {
              cellData[rowIndex] = {};
            }
            
            const univerCell: any = { v: cell.v };
            
            // Map type: s=string (1), n=number (2), b=boolean (3)
            if (cell.t === 's') univerCell.t = 1;
            else if (cell.t === 'n') univerCell.t = 2;
            else if (cell.t === 'b') univerCell.t = 3;
            else univerCell.t = 1;
            
            if (cell.f) univerCell.f = cell.f;
            
            cellData[rowIndex][colIndex] = univerCell;
          }
        });
      }
    });
    
    console.log('📦 Converted cells:', Object.keys(cellData).length, 'rows');

    // Simple hard-coded data
    const workbookData = {
      id: 'test-workbook',
      name: 'Test Workbook - 100 Rows',
      appVersion: '0.1.0',
      sheets: {
        sheet1: {
          id: 'sheet1',
          name: realData.name,
          cellData,
          rowCount: realData.rowCount,
          columnCount: realData.columnCount,
          defaultColumnWidth: 73,
          defaultRowHeight: 23,
          zoomRatio: 1,
          scrollTop: 0,
          scrollLeft: 0,
          freeze: {
            startRow: -1,
            startColumn: -1,
            ySplit: 0,
            xSplit: 0,
          },
          mergeData: [],
          rowData: {},
          columnData: {},
          rowHeader: {
            width: 46,
            hidden: 0,
          },
          columnHeader: {
            height: 20,
            hidden: 0,
          },
          showGridlines: 1,
          hidden: 0,
          tabColor: '',
          rightToLeft: 0,
        }
      },
      sheetOrder: ['sheet1']
    };

      console.log('📊 Creating workbook with data:', workbookData);

      try {
        await createWorkbookWithData(univer, workbookData);
        console.log('✅ Workbook created successfully with editing enabled!');
      } catch (error) {
        console.error('❌ Error creating workbook:', error);
      }
    };

    initUniver();

    return () => {
      console.log('🧹 Disposing Univer');
      if (univerRef.current) {
        univerRef.current.dispose();
        univerRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '600px', border: '1px solid #ccc' }}>
      <h2>Univer Simple Test</h2>
      <div ref={containerRef} style={{ width: '100%', height: '500px' }} />
    </div>
  );
};

